// Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaPhoneAlt, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaClock,
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaHeart,
  FaWifi,
  FaCoffee,
  FaParking,
  FaShieldAlt
} from 'react-icons/fa';
import { HiOutlineChevronRight } from 'react-icons/hi';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Paridhi', path: '/about' },
    { name: 'Rooms in Varanasi', path: '/rooms' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Book Your Stay', path: '/booking' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const amenities = [
    { icon: FaWifi, name: 'Free WiFi' },
    { icon: FaCoffee, name: 'Free Breakfast' },
    { icon: FaParking, name: 'Free Parking' },
    { icon: FaShieldAlt, name: '24/7 Security' },
  ];

  const socialLinks = [
    { icon: FaFacebookF, name: 'Facebook', link: 'https://www.facebook.com/profile.php?id=61575374333868#' },
    { icon: FaInstagram, name: 'Instagram', link: 'https://www.instagram.com/paridhiguesthouse/?fbclid=IwY2xjawRBw69leHRuA2FlbQIxMQBicmlkETFLV294bzY3ZmVDQjhnUDNEc3J0YwZhcHBfaWQBMAABHl_Vh8LSSja_H0NqKBcCb_BmEPZgUgBYDWVsteelqd0i2ybYuZfi4zVk8UZ9_aem_9ssFAnfQ1zDIdx_fR1L0Uw' },
    { icon: FaTwitter, name: 'Twitter', link: 'https://twitter.com' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-400">
      {/* Top Border */}
      <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Column 1 - Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img 
                src="/logo.png" 
                alt="Paridhi Guest House" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center"><span class="text-white font-bold text-base">PG</span></div>';
                }}
              />
              <div>
                <h3 className="text-white font-bold text-lg">Paridhi Guest House</h3>
                <p className="text-xs text-gray-500">Luxury & Comfort Redefined</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              Experience luxury and comfort in the heart of Varanasi. Your home away from home.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-800 text-gray-400 w-8 h-8 rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all hover:scale-110"
                >
                  <social.icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 border-l-2 border-blue-500 pl-3">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1"
                  >
                    <HiOutlineChevronRight size={12} className="text-blue-500" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 border-l-2 border-blue-500 pl-3">
              Contact Info
            </h4>
            <div className="space-y-3">
              <div className="flex gap-2">
                <FaPhoneAlt className="text-blue-500 mt-0.5 text-sm" />
                <div>
                  <a href="tel:+919555040589" className="text-sm text-gray-400 hover:text-blue-400 transition block">
                    095550 40589
                  </a>
                </div>
              </div>
              <div className="flex gap-2">
                <FaEnvelope className="text-blue-500 mt-0.5 text-sm" />
                <a href="mailto:paridhiguest@gmail.com" className="text-sm text-gray-400 hover:text-blue-400 transition break-all">
                  paridhiguest@gmail.com
                </a>
              </div>
              <div className="flex gap-2">
                <FaMapMarkerAlt className="text-blue-500 mt-0.5 text-sm" />
                <p className="text-sm text-gray-400 leading-relaxed">
                  Sri Ram Nagar Colony, Mahmoorganj,<br />
                  Varanasi - 221106, UP
                </p>
              </div>
              <div className="flex gap-2">
                <FaClock className="text-blue-500 mt-0.5 text-sm" />
                <p className="text-sm text-gray-400">
                  Check-in: 12 PM | Check-out: 11 AM<br />
                  24/7 Guest Support
                </p>
              </div>
            </div>
          </div>

          {/* Column 4 - Amenities */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 border-l-2 border-blue-500 pl-3">
              Amenities
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {amenities.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <item.icon className="text-blue-500 text-sm" />
                  <span className="text-sm text-gray-400">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-gray-500">
            © {currentYear} Paridhi Guest House. All rights reserved.
          </p>
          <div className="flex gap-4 text-xs">
            <Link to="/privacy" className="text-gray-500 hover:text-blue-400 transition">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-blue-400 transition">Terms & Conditions</Link>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            Made with <FaHeart className="text-red-500 text-xs" /> in Varanasi
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;